import {
  SortingState,
  flexRender,
  getCoreRowModel,
  useReactTable,
  getSortedRowModel,
  Updater,
} from "@tanstack/react-table";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../components/table";
import { twMerge } from "tailwind-merge";

function LoadingSkeleton({ columnLength }: { columnLength: number }) {
  return (
    <>
      {Array.from({ length: 7 }).map((_, i) => (
        <tr
          key={i}
          className="bg-white [&_td]:py-3 [&_td_div]:rounded-[6px] [&_td]:border-b [&_td]border-b-[#E5E7EB]"
        >
          {Array.from({ length: columnLength }).map((_, i) => (
            <td key={i}>
              <div className="w-24 h-4 bg-slate-200 animate-pulse transition duration-50" />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}

export function DataTable({
  columns,
  onRowClick,
  data,
  isLoading,
  footerContent,
  scrollContainerRef,
  className,
  isRowClickable,
  sorting = [],
  setSorting,
  getRowId,
  loadingSkeleton,
}: {
  columns: any;
  onRowClick?: any;
  data: any;
  isLoading: boolean;
  footerContent?: React.ReactNode;
  scrollContainerRef?: React.RefObject<HTMLDivElement>;
  className?: string;
  isRowClickable?: (row: any) => boolean;
  sorting?: SortingState;
  setSorting?: (updater: SortingState | ((old: SortingState) => SortingState)) => void;
  /** Stable row id for reconciliation (e.g. (row) => row.assignmentID) */
  getRowId?: (row: any, index: number) => string;
  loadingSkeleton?: React.ReactNode;
}) {

  const handleSortingChange = (updaterOrValue: Updater<SortingState>) => {
    setSorting?.((old) =>
      typeof updaterOrValue === "function" ? updaterOrValue(old) : updaterOrValue
    );
  };

  const table = useReactTable({
    data: data || [],
    columns,
    getRowId,
    getCoreRowModel: getCoreRowModel(),
    onSortingChange: handleSortingChange,
    // Without `setSorting` there is no sorted row model, so a column that opted into
    // sorting would toggle its header indicator without ever reordering rows.
    ...(setSorting
      ? {
        getSortedRowModel: getSortedRowModel(),
        manualSorting: false,
        state: {
          sorting,
        },
        enableSorting: true,
      }
      : { enableSorting: false }),
  });
  return (
    <div
      ref={scrollContainerRef}
      className={twMerge("flex flex-col data-table-container lg:h-[calc(100dvh-275px)] h-[60vh] [&>div]:bg-white overflow-auto", className)}
    >
      <Table className="bg-white table">
        <TableHeader>
          {table?.getHeaderGroups()?.map((headerGroup) => (
            <TableRow
              className="table-head-row !rounded-[9px] sticky top-0 z-[1] border-b border-lightGray bg-white"
              key={headerGroup.id}
            >
              {headerGroup.headers.map((header, i) => {
                return (
                  <TableHead
                    className={twMerge("table-head !h-12 text-headerPrimary text-sm font-semibold uppercase", i === 0 ? "rounded-l-[10px]" : "", i === headerGroup.headers.length - 1 ? "rounded-r-md" : "")}
                    key={header.id}
                    style={header.column.columnDef.size != null ? { width: `${header.getSize()}px` } : undefined}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                  </TableHead>
                );
              })}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {isLoading ? (
            loadingSkeleton || <LoadingSkeleton columnLength={columns.length} />
          ) : (
            <>
              {table?.getRowModel()?.rows?.length > 0 &&
                table?.getRowModel()?.rows?.map((row) => (
                  <TableRow
                    key={row.id}
                    data-state={row.getIsSelected() && "selected"}
                    onClick={() => onRowClick && (isRowClickable == null || isRowClickable(row.original)) && onRowClick(row.original)}
                    className={twMerge("cursor-pointer", isRowClickable != null && !isRowClickable(row.original) ? "cursor-not-allowed" : "hover:bg-progressHover")}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              {table?.getRowModel()?.rows?.length === 0 && (
                <TableRow>
                  <TableCell colSpan={columns.length} className="h-24 text-center">
                    No results.
                  </TableCell>
                </TableRow>
              )}
            </>
          )}
        </TableBody>
      </Table>
      {footerContent}
    </div>
  );
}
